import {
  alpha,
  chipClasses,
  ChipProps,
  Components,
  createTheme,
  CSSProperties,
  Theme,
  ThemeOptions,
} from "@mui/material";
import { common } from "@mui/material/colors";
import { deepmerge } from "@mui/utils";
import merge from "lodash/merge";

import { zIndexTokens } from "../tokens";
import { tokens } from "../tokens/generated/tokens";

import { BRAND_COLORS } from "./brand";
import { paletteVar, paletteVarAlpha } from "./palette-var";

import type { PaletteColorOptions } from "@mui/material/styles";

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    label: true;
    plain: true;
  }
  interface ChipPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}

// Components that take `brandPrimary` / `brandSupporting` as a `color`; see the
// note on `Palette.brandPrimary` below.
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/Checkbox" {
  interface CheckboxPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/Radio" {
  interface RadioPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/Switch" {
  interface SwitchPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/CircularProgress" {
  interface CircularProgressPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/Pagination" {
  interface PaginationPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/InputBase" {
  interface InputBasePropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/FormLabel" {
  interface FormLabelPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}
declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    brandPrimary: true;
    brandSupporting: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    /**
     * The design system's own colour vocabulary, deliberately separate from
     * MUI's `primary`/`secondary`.
     *
     * Those two are inverted in this codebase — `primary` holds the supporting
     * teal and `secondary` holds the brand purple — so a base component reading
     * them renders the wrong colour. Base components read `brand` instead: every
     * theme supplies it, mapping whatever it calls these colours onto names that
     * say what they are. Where a MUI `color` prop needs a palette colour, use
     * `brandPrimary` / `brandSupporting` below.
     *
     * Optional so a theme that predates it (a bare `createTheme`, a consuming
     * app mid-rollout) type-checks; read it through `brand()`, never directly.
     */
    brand?: {
      /** The Centro purple. Design's `fgColor.primary`. */
      primary: string;
      /**
       * Darkened purple for hover and active states. Needed because base
       * components paint `primary` via `sx`, which opts out of MUI's automatic
       * hover darkening.
       */
      primaryHover: string;
      /** Readable label/icon colour on a filled brand-primary surface. */
      onPrimary: string;
      /** The supporting teal. Design's `fgColor.secondary`. */
      supporting: string;
    };
    surfaces: {
      high: string;
      neutral: string;
      primary: string;
      secondary: string;
    };
    dropzone: {
      lineSoft: string;
      lineHard: string;
      error: string;
    };
    neutrals: {
      neutral_50: string;
      neutral_100: string;
      neutral_200: string;
      neutral_300: string;
      neutral_400: string;
      neutral_500: string;
      neutral_600: string;
      neutral_700: string;
      neutral_800: string;
      neutral_900: string;
    };
    ai: {
      infoLabel: string;
      extracted: string;
      comment: string;
      reviewBackground: string;
      validatedBackground: string;
      extractionError: string;
      edited: string;
      chips: {
        default: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        primary: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        success: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        warning: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        info: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
      };
    };
  }
  interface Palette {
    /**
     * The two brand colours under design's names, matching the keys of
     * `brand`. This theme's `primary` is teal and `secondary` purple; design's
     * tokens, and the Kaizen theme, have them the other way round. Code that
     * means the brand purple reads `brandPrimary` so it renders purple under
     * both, rather than `secondary`, which turns teal under Kaizen.
     */
    brandPrimary: PaletteColor;
    /** The supporting teal; see `brandPrimary`. */
    brandSupporting: PaletteColor;
  }
  interface PaletteColor {
    medium: string;
  }
  interface SimplePaletteColorOptions {
    medium?: string;
  }
  interface PaletteOptions {
    brandPrimary?: PaletteColorOptions;
    brandSupporting?: PaletteColorOptions;
    /** See the `brand` note on `Palette` above. */
    brand?: {
      primary: string;
      primaryHover: string;
      onPrimary: string;
      supporting: string;
    };
    dropzone?: {
      lineSoft: string;
      lineHard: string;
      error: string;
    };
    surfaces?: {
      high: string;
      neutral: string;
      primary: string;
      secondary: string;
    };
    neutrals?: {
      neutral_50: string;
      neutral_100: string;
      neutral_200: string;
      neutral_300: string;
      neutral_400: string;
      neutral_500: string;
      neutral_600: string;
      neutral_700: string;
      neutral_800: string;
      neutral_900: string;
    };
    ai?: {
      infoLabel: string;
      extracted: string;
      comment: string;
      reviewBackground: string;
      validatedBackground: string;
      extractionError: string;
      edited: string;
      chips: {
        default: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        primary: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        success: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        warning: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
        info: {
          backgroundColor: string;
          borderColor: string;
          color: string;
        };
      };
    };
  }
}

interface CustomComponents extends Components<Omit<Theme, "components">> {
  MuiBox?: {
    styleOverrides?: {
      root?: React.CSSProperties;
    };
  };
}

// The brand purple's dark shade, used for hover/active states on both the
// primary button gradient and primary links.
const BRAND_PRIMARY_DARK = BRAND_COLORS.primaryHover;

// This theme's two brand colours, under its own (inverted) role names below
// and under `brandSupporting` / `brandPrimary`. Built from the same objects so
// the two names cannot drift apart.
const legacyTeal = {
  main: "#4FB3CD",
  dark: "#00344F",
  medium: "#0078A2",
  light: "#4FB3CD",
};
const legacyPurple = { main: "#832A80" };
const { augmentColor } = createTheme().palette;

const palette = {
  // This theme calls the brand purple `secondary` and the supporting teal
  // `primary` — backwards, but consistent throughout the legacy codebase.
  // `brand` names them correctly so base components never have to know.
  brand: BRAND_COLORS,
  error: {
    main: "#DF4C32",
  },
  neutrals: {
    neutral_50: "#fafafa",
    neutral_100: "#f7f7f7",
    neutral_200: "#f1f0f0",
    neutral_300: "#e0e0e0",
    neutral_400: "#bdbdbd",
    neutral_500: "#9e9e9e",
    neutral_600: "#757575",
    neutral_700: "#58585a",
    neutral_800: "#424242",
    neutral_900: "#212121",
  },
  surfaces: {
    high: "#fdeded",
    neutral: "#f2f2f2",
    primary: "#EFF8FB",
    secondary: "#FAF0FA",
  },
  ai: {
    infoLabel: "#404040",
    extracted: "#42A5F5",
    comment: "#F57F17",
    reviewBackground: "#FFFDE7",
    validatedBackground: "#E8F5E9",
    extractionError: "#E8A020",
    edited: "#9E9E9E",
    chips: {
      default: {
        backgroundColor: "#F5F5F5",
        borderColor: "#F5F5F5",
        color: "#757575",
      },
      primary: {
        backgroundColor: "#0078A21A",
        borderColor: "#0078A21A",
        color: "#0078A2",
      },
      success: {
        backgroundColor: "#4CAF501A",
        borderColor: "#4CAF501A",
        color: "#4CAF50",
      },
      info: {
        backgroundColor: "grey.100",
        borderColor: "grey.100",
        color: "black",
      },
      warning: {
        backgroundColor: "#FF98001A",
        borderColor: "#FF98001A",
        color: "#FF9800",
      },
    },
  },
  primary: {
    ...legacyTeal,
    required: "#df4c32",
  },
  secondary: legacyPurple,
  brandSupporting: augmentColor({ color: legacyTeal }),
  brandPrimary: augmentColor({ color: legacyPurple }),
  text: {
    primary: "#00344F",
    secondary: "#757575",
  },
};

// Colours referenced as custom properties so they follow the active colour
// scheme; see `palette-var.ts`. Each is paired with the slot holding the same
// colour as its fallback, which because of the inversion documented on
// `Palette` above is not the slot with the matching name — this theme's
// `secondary` purple is design's `primary`, and its `primary` teal is design's
// `secondary`.
const brandPrimaryVar = paletteVar("primary-main", palette.secondary.main);
const brandPrimaryDarkVar = paletteVar("primary-dark", BRAND_PRIMARY_DARK);
const brandSupportingVar = paletteVar("secondary-main", palette.primary.medium);
const textPrimaryVar = paletteVar("text-primary", palette.text.primary);
const textSecondaryVar = paletteVar("text-secondary", palette.text.secondary);
const errorMainVar = paletteVar("error-main", palette.primary.required);
const surfacesPrimaryVar = paletteVar(
  "surfaces-primary",
  palette.surfaces.primary,
);
// The app chrome's ground. `neutrals.neutral_50` and design's
// `background.default` are both #fafafa, so the fallback is the colour this
// renders today.
const groundVar = paletteVar("background-default", palette.neutrals.neutral_50);

const fontFamily = "Montserrat, Roman, Arial, sans-serif";

const typography = {
  fontFamily,
  h1: {
    fontFamily,
    color: textPrimaryVar,
    fontSize: 40,
    fontWeight: "heavy",
    lineHeight: "130%",
    fontStyle: "normal",
    letterSpacing: 5,
  },
  h2: {
    fontFamily,
    color: textPrimaryVar,
    fontSize: 32,
    fontWeight: "heavy",
    lineHeight: "130%",
    letterSpacing: 3,
  },
  h3: {
    fontFamily,
    color: textPrimaryVar,
    fontSize: 24,
    fontWeight: "heavy",
    lineHeight: "150%",
    letterSpacing: 2,
  },
  h4: {
    fontFamily,
    color: textPrimaryVar,
    fontSize: 20,
    fontWeight: "heavy",
    lineHeight: "150%",
    letterSpacing: 1,
  },
  h5: {
    fontFamily,
    color: textPrimaryVar,
    fontSize: 24,
    fontWeight: "heavy",
  },
  h6: {
    fontFamily,
    color: textPrimaryVar,
    fontSize: 20,
    fontWeight: "heavy",
  },
  body1: {
    fontFamily,
    color: textPrimaryVar,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "150%",
    letterSpacing: 0.7,
  },
  body2: {
    fontFamily,
    color: textPrimaryVar,
  },
};

const sharedThemeOptions: ThemeOptions = {
  palette,
  typography: {
    ...typography,
  },
  zIndex: {
    mobileStepper: zIndexTokens.dropdown,
    fab: zIndexTokens.sticky,
    speedDial: zIndexTokens.sticky,
    appBar: zIndexTokens.appBar,
    drawer: zIndexTokens.drawer,
    modal: zIndexTokens.modal,
    snackbar: zIndexTokens.snackbar,
    tooltip: zIndexTokens.tooltip,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
        html: {
          scrollBehavior: "smooth",
        },
        "body, html, #root": {
          height: "100%",
          backgroundColor: groundVar,
          fontFamily: typography.fontFamily,
        },
        // Inline emphasis is markup, not a variant. Without this rule `strong`
        // takes the browser's `bolder`, which lands on 700 under a 400 body and
        // on 900 under a 600 title; one emphasis weight applies to every role.
        "strong, b": {
          fontWeight: tokens.base.typography.text.weight.semibold,
        },
        "em, i": {
          fontStyle: "italic",
        },
        "#root": {
          display: "flex",
          flexDirection: "column",
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&.no-uppercase": {
            textTransform: "none",
          },
          fontFamily: "inherit",
        },
        outlined: {
          "& > a.csv-link": {
            color: brandPrimaryVar,
          },
        },
        contained: {
          background: `linear-gradient(270deg, ${brandPrimaryDarkVar} 1.35%, ${brandPrimaryVar} 100%)`,
          color: common.white,
          "&.Mui-disabled": {
            background: "rgba(0, 0, 0, 0.12)",
          },
        },
        text: {
          color: brandPrimaryVar,
        },
        link: {
          color: brandPrimaryVar,
          textTransform: "initial",
          padding: 0,
          letterSpacing: "inherit",
        },
      },
      defaultProps: {
        variant: "contained",
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          wordBreak: "break-word",
        },
      },
      // The shared palette's `primary` (#4FB3CD) predates the brand purple;
      // `secondary` (#832A80) is the actual brand color, so `color="primary"`
      // is remapped here the same way MuiButton's `contained` gradient is.
      variants: [
        {
          props: { color: "primary" },
          style: {
            color: brandPrimaryVar,
            // MUI derives the underline's color from this custom property
            // (set from `palette.primary.main` in its own base styles), not
            // from the `color` override above — it has to be restated here,
            // in both the resting and hover states.
            "--Link-underlineColor": paletteVarAlpha(brandPrimaryVar, 0.4),
            "&:hover": {
              color: brandPrimaryDarkVar,
              "--Link-underlineColor": paletteVarAlpha(
                brandPrimaryDarkVar,
                0.4,
              ),
            },
          },
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          // Scheme-aware; the fallback is the white it rendered before, so a
          // theme without the tokens is unchanged and the bar follows the dark
          // scheme without an overlay rule.
          backgroundColor: paletteVar("background-paper", "#ffffff"),
          ".MuiTabs-root": {
            alignSelf: "stretch",
            a: {
              minWidth: "auto",
              paddingBottom: 0,
              paddingTop: "1.8rem",
              paddingLeft: 0,
              paddingRight: 0,
              "&:not(:first-of-type)": {
                marginLeft: 0,
              },
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          "@media (min-width: 600px)": {
            padding: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          // The primary navigation bar's tabs. `PrimaryBar` sets the class; a
          // page can restyle the bar through it without reaching into the
          // component.
          "&.app-bar-tabs": {
            display: "flex",
            alignItems: "center",
            ".MuiTabs-indicator": {
              height: 2,
              marginBottom: 10,
            },
            ".MuiButtonBase-root": {
              color: brandSupportingVar,
              borderBottomColor: brandSupportingVar,
              padding: "1rem 1rem 1rem 1rem",
              "&:not(.Mui-selected)": {
                color: textSecondaryVar,
              },
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: surfacesPrimaryVar,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 0,
          marginRight: 5,
          color: textPrimaryVar,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            // `action-selected` is the scheme-aware selected overlay, a
            // translucent tint visible on any surface in light or dark. The
            // pale `surfaces.primary` literal reads as near-white in dark and
            // washes out the selected row, so it stays only as the fallback.
            backgroundColor: paletteVar(
              "action-selected",
              palette.surfaces.primary,
            ),
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: common.white,
            backgroundColor: brandPrimaryVar,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          "&.Mui-active, &.Mui-completed": {
            color: brandPrimaryVar,
          },
        },
        text: {
          color: common.white,
          fill: common.white,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
          color: textPrimaryVar,
          opacity: 0.54,
          "&.Mui-focused": {
            color: textPrimaryVar,
            opacity: 0.54,
          },
          "&.Mui-required": {
            "& .MuiFormLabel-asterisk": {
              color: errorMainVar,
            },
          },
          "& .required": {
            color: errorMainVar,
          },
          "&.Mui-error": {
            color: textPrimaryVar,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          // Scheme-aware so inputs follow the color scheme instead of staying a
          // hardcoded white box; the fallback is the white this rendered before,
          // so themes without cssVariables are unchanged.
          backgroundColor: paletteVar("background-paper", "#ffffff"),
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          marginBottom: 8,
          color: textPrimaryVar,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          "&.with-underline": {
            textDecoration: "underline",
          },
          "&.primary-color": {
            color: brandSupportingVar,
          },
          "& .required": {
            color: errorMainVar,
          },
          "&.secondary-color": {
            color: brandPrimaryVar,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 1,
          variants: [
            {
              props: (props) =>
                props.variant === "label" || props.variant === "plain",
              style: ({
                theme,
                ownerState,
              }: {
                theme: Theme;
                ownerState: ChipProps;
              }) => {
                const chipColor = ownerState.color || "default";

                const cssProperties: CSSProperties = {
                  backgroundColor: "transparent",
                  border: "none",
                  color:
                    ownerState.variant === "plain"
                      ? textPrimaryVar
                      : textSecondaryVar,
                };

                if (chipColor !== "default" && theme.palette[chipColor]) {
                  const themeColor = theme.palette[chipColor].main;
                  merge(cssProperties, {
                    color: themeColor,
                    [`&.${chipClasses.clickable}:hover`]: {
                      backgroundColor: alpha(themeColor, 0.2),
                    },
                    [`&.${chipClasses.clickable}:focus-visible`]: {
                      backgroundColor: alpha(themeColor, 0.3),
                    },
                    [`& .${chipClasses.deleteIcon}`]: {
                      color: alpha(themeColor, 0.7),
                      "&:hover": {
                        color: themeColor,
                      },
                    },
                  });
                }
                return cssProperties;
              },
            },
          ],
        },
      },
    },
  } as CustomComponents,
};

function createSharedTheme(options?: ThemeOptions): Theme {
  return createTheme(deepmerge(sharedThemeOptions, options));
}

export {
  createSharedTheme,
  fontFamily,
  palette,
  sharedThemeOptions,
  typography,
};
